import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Users, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/app/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/app/components/ui/dialog';
import { cn } from '@/app/components/ui/utils';
import { useProjects, useClients, useCreateProject, useCreateProjectAssignment } from '@/hooks/use-projects';
import { useTechProfiles } from '@/hooks/use-crew';
import { usePlatformModules } from '@/hooks/use-modules';
import { usePermissions } from '@/hooks/use-permissions';
import { useAllCFLocations } from '@/hooks/use-cf-locations';
import { useOrgToday } from '@/hooks/use-settings';
import { handleApiError } from '@/lib/api-client';
import { formatDateOnly } from '@/lib/date-utils';
import type { Project, ProjectStatus } from '@/lib/types';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  active: 'bg-green-100 text-green-800',
  on_hold: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-blue-100 text-blue-800',
  cancelled: 'bg-red-100 text-red-800',
};

function formatDate(dateStr: string | null | undefined) {
  return formatDateOnly(dateStr);
}

export function JobsTab() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [clientFilter, setClientFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignProjectId, setAssignProjectId] = useState<number | null>(null);

  const { data, isLoading, isError } = useProjects({
    search: search || undefined,
    status: (statusFilter as ProjectStatus) || undefined,
    client: clientFilter ? parseInt(clientFilter) : undefined,
    page,
    page_size: 25,
  });

  useEffect(() => {
    if (isError) {
      toast.error('Failed to load projects');
    }
  }, [isError]);

  const { data: clientsData } = useClients({ page_size: 100 });
  const { isModuleEnabled } = usePlatformModules();
  const { hasMinRole } = usePermissions();
  const projectsEnabled = isModuleEnabled('projects');
  const safetyEnabled = isModuleEnabled('safety');
  const canCreateJob = hasMinRole('Manager');
  const columnCount = 8 + (safetyEnabled ? 1 : 0) + (projectsEnabled ? 1 : 0);

  const handleRowClick = useCallback(
    (project: Project) => {
      navigate(`/projects/${project.id}`);
    },
    [navigate]
  );

  const handleAssignPeople = (e: React.MouseEvent, projectId: number) => {
    e.stopPropagation();
    setAssignProjectId(projectId);
    setAssignOpen(true);
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row sm:flex-1 gap-2 sm:gap-3 sm:items-center">
          <div className="relative w-full sm:flex-1 sm:min-w-[200px] sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or number..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v === 'all' ? '' : v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-full sm:w-[140px]" size="sm">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="on_hold">On Hold</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          {clientsData?.results && clientsData.results.length > 0 && (
            <Select
              value={clientFilter}
              onValueChange={(v) => {
                setClientFilter(v === 'all' ? '' : v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-full sm:w-[160px]" size="sm">
                <SelectValue placeholder="Client" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {clientsData.results.map((client) => (
                  <SelectItem key={client.id} value={String(client.id)}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        {canCreateJob && (
          <button
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create Project
          </button>
        )}
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left px-4 py-3 font-medium text-gray-600">Project Name</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Number</th>
                {projectsEnabled && (
                  <th className="text-left px-4 py-3 font-medium text-gray-600">PO Number</th>
                )}
                <th className="text-left px-4 py-3 font-medium text-gray-600">Location</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Client</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600"># People</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Date Range</th>
                {safetyEnabled && (
                  <th className="text-left px-4 py-3 font-medium text-gray-600">JSAs</th>
                )}
                <th className="text-left px-4 py-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b animate-pulse">
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-32" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-16" /></td>
                    {projectsEnabled && (
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-16" /></td>
                    )}
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-24" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-20" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-16" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-8" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-28" /></td>
                    {safetyEnabled && (
                      <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-8" /></td>
                    )}
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-14" /></td>
                  </tr>
                ))
              ) : !data?.results?.length ? (
                <tr>
                  <td colSpan={columnCount} className="px-4 py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-100">
                        <Briefcase className="h-6 w-6 text-gray-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">No projects yet</p>
                        <p className="text-sm text-gray-500 mt-1">Create your first project to start organizing work.</p>
                      </div>
                      {canCreateJob && (
                        <button
                          onClick={() => setCreateOpen(true)}
                          className="mt-2 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <Plus className="h-4 w-4" />
                          Create Project
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                data.results.map((project) => (
                  <tr
                    key={project.id}
                    onClick={() => handleRowClick(project)}
                    className="border-b hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 font-medium text-gray-900">{project.name}</td>
                    <td className="px-4 py-3 text-gray-600">{project.project_number || '—'}</td>
                    {projectsEnabled && (
                      <td className="px-4 py-3 text-gray-600">{project.po_number || '—'}</td>
                    )}
                    <td className="px-4 py-3 text-gray-600">{project.location || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{project.client_name || '—'}</td>
                    <td className="px-4 py-3">
                      <Badge
                        className={cn(
                          'capitalize',
                          statusColors[project.status] || 'bg-gray-100 text-gray-800'
                        )}
                      >
                        {project.status?.replace('_', ' ')}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      —
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {formatDate(project.start_date)} – {project.end_date ? formatDate(project.end_date) : <span className="text-blue-600 font-medium">Ongoing</span>}
                    </td>
                    {safetyEnabled && (
                      <td className="px-4 py-3 text-gray-600">
                        —
                      </td>
                    )}
                    <td className="px-4 py-3">
                      {projectsEnabled && (
                        <button
                          onClick={(e) => handleAssignPeople(e, project.id)}
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 transition-colors"
                          title="Assign People"
                        >
                          <Users className="h-3.5 w-3.5" />
                          Assign
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {data && (data.next || data.previous) && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            {data.count} total result{data.count !== 1 ? 's' : ''}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={!data.previous}
              className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!data.next}
              className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Create Project Modal */}
      <CreateJobModal open={createOpen} onOpenChange={setCreateOpen} />

      {/* Assign People Modal */}
      {assignProjectId !== null && (
        <AssignPeopleModal
          open={assignOpen}
          onOpenChange={(open) => {
            setAssignOpen(open);
            if (!open) setAssignProjectId(null);
          }}
          projectId={assignProjectId}
        />
      )}
    </div>
  );
}

// ─── Create Project Modal ───────────────────────────────────────────────────────

function CreateJobModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [name, setName] = useState('');
  const [projectNumber, setProjectNumber] = useState('');
  const [locationFkId, setLocationFkId] = useState<string>('');
  const [clientId, setClientId] = useState<string>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [ongoing, setOngoing] = useState(false);
  const [status, setStatus] = useState<string>('draft');
  const [formError, setFormError] = useState<string | null>(null);

  const { data: clientsData } = useClients({ page_size: 100 });
  const { data: cfLocations } = useAllCFLocations();
  const { isModuleEnabled } = usePlatformModules();
  const projectsEnabled = isModuleEnabled('projects');
  const createMutation = useCreateProject();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    try {
      await createMutation.mutateAsync({
        name,
        location_fk: locationFkId ? parseInt(locationFkId) : undefined,
        client: clientId ? parseInt(clientId) : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        status: status as ProjectStatus,
        // PO Number is an H3Books field — omit it when the module is off or
        // the server rejects the whole payload.
        ...(projectsEnabled && projectNumber
          ? { po_number: projectNumber }
          : {}),
      });
    } catch (err) {
      const apiError = handleApiError(err);
      const fieldMessages = Object.entries(apiError.errors ?? {})
        .map(([field, messages]) => `${field}: ${messages.join(' ')}`)
        .join('; ');
      setFormError(fieldMessages || apiError.message);
      return;
    }

    setName('');
    setProjectNumber('');
    setLocationFkId('');
    setClientId('');
    setStartDate('');
    setEndDate('');
    setOngoing(false);
    setStatus('draft');
    toast.success('Project created');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Project</DialogTitle>
          <DialogDescription>
            Add a new project to your organization.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {projectsEnabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PO Number</label>
                <input
                  type="text"
                  value={projectNumber}
                  onChange={(e) => setProjectNumber(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}
            <div className={projectsEnabled ? '' : 'col-span-2'}>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <Select value={locationFkId} onValueChange={setLocationFkId}>
                <SelectTrigger size="sm">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {(cfLocations ?? []).map((loc) => (
                    <SelectItem key={loc.id} value={String(loc.id)}>
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger size="sm">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clientsData?.results?.map((client) => (
                    <SelectItem key={client.id} value={String(client.id)}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger size="sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={ongoing}
                className={cn(
                  'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500',
                  ongoing && 'opacity-50 cursor-not-allowed',
                )}
              />
              <label className="flex items-center gap-2 mt-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={ongoing}
                  onChange={(e) => {
                    setOngoing(e.target.checked);
                    if (e.target.checked) setEndDate('');
                  }}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-600">Ongoing (no end date)</span>
              </label>
            </div>
          </div>
          {formError && (
            <div
              className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
              role="alert"
            >
              {formError}
            </div>
          )}
          <DialogFooter>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Project'}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Assign People Modal ────────────────────────────────────────────────────

function AssignPeopleModal({
  open,
  onOpenChange,
  projectId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: number;
}) {
  const orgToday = useOrgToday();
  const [selectedTechs, setSelectedTechs] = useState<number[]>([]);
  const [search, setSearch] = useState('');

  const { data: techsData } = useTechProfiles({
    q: search || undefined,
    page_size: 50,
    status: 'active',
  });

  const assignMutation = useCreateProjectAssignment();

  const toggleTech = (id: number) => {
    setSelectedTechs((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };

  const handleAssign = async () => {
    for (const techId of selectedTechs) {
      const assignment = await assignMutation.mutateAsync({
        project: projectId,
        tech: techId,
        role: 'technician',
        start_date: orgToday,
      });
      if (assignment.warnings?.length) {
        toast.warning('Assignment created with warnings', {
          description: assignment.warnings.map((warning) => warning.message).join(' '),
        });
      }
    }
    setSelectedTechs([]);
    setSearch('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Assign People</DialogTitle>
          <DialogDescription>
            Select people to assign to this job.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search people..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div
            className="max-h-60 overflow-y-auto border rounded-lg p-2 space-y-1"
            data-required="true"
            data-required-empty={selectedTechs.length === 0 ? 'true' : 'false'}
            data-required-message="Select at least one person."
            tabIndex={-1}
          >
            {techsData?.results?.map((tech) => (
              <label
                key={tech.id}
                className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 px-2 py-1.5 rounded"
              >
                <input
                  type="checkbox"
                  checked={selectedTechs.includes(tech.id)}
                  onChange={() => toggleTech(tech.id)}
                  className="rounded border-gray-300"
                />
                <span>
                  {tech.first_name} {tech.last_name}
                </span>
                <span className="text-gray-400 text-xs">{tech.email}</span>
              </label>
            ))}
            {!techsData?.results?.length && (
              <p className="text-sm text-gray-500 text-center py-4">No people found.</p>
            )}
          </div>
          {selectedTechs.length > 0 && (
            <p className="text-sm text-gray-600">{selectedTechs.length} selected</p>
          )}
        </div>
        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={assignMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {assignMutation.isPending ? 'Assigning...' : 'Assign Selected'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
